import { useRef, useMemo, useState, useEffect } from 'react';
import * as echarts from 'echarts/core';
import { cloneDeep } from 'lodash-es';
import useChartResize from './useChartResize';
import useChartRefreshWhenOptionChanged, { ISetOptionConfigs } from './useChartRefreshWhenOptionChanged';
import { LANGUAGE_EN, LANGUAGE_CN } from '@home/_config/config';

import type { EChartsType } from 'echarts';

/**
 * echarts内部的语言环境类型
 */
type IEChartLocale = 'ZH' | 'EN';

/**
 * echarts图表locale映射
 * 键为databrain系统的语言环境，值为echarts内部的语言环境
 */
const ECHARTS_LOCALE_MAP: IRecord<IEChartLocale> = {
  [LANGUAGE_EN]: 'EN',
  [LANGUAGE_CN]: 'ZH',
};

/**
 * echarts内部语言环境（默认值）(英文)
 */
const DEFAULT_ECHARTS_LOCALE = ECHARTS_LOCALE_MAP[LANGUAGE_EN];

/**
 * echarts图表初始化配置
 */
const ECHARTS_INIT_CONFIGS = {
  /**
   * 设备像素比，默认取浏览器的值window.devicePixelRatio
   */
  devicePixelRatio: window.devicePixelRatio,
  /**
   * 渲染模式，支持'canvas'、'svg'
   */
  renderer: 'canvas',
  /**
   * 是否使用服务端渲染，只有在 SVG 渲染模式有效。开启后不再会每帧自动渲染，必须要调用 renderToSVGString 方法才能得到渲染后 SVG 字符串。
   */
  ssr: false,
  /**
   * 是否开启脏矩形渲染，只有在canvas渲染模式有效，默认为false
   */
  useDirtyRect: false,
  /**
   * 可显式指定实例宽度，单位为像素。如果传入值为null/undefined/'auto'，则表示自动取 dom（实例容器）的宽度
   */
  width: undefined,
  /**
   * 可显式指定实例高度，单位为像素。如果传入值为null/undefined/'auto'，则表示自动取 dom（实例容器）的高度
   */
  height: undefined,
  /**
   * 使用的语言，内置 'ZH' 和 'EN' 两个语言
   */
  locale: undefined,
};

/**
 * 组件加载时初始化echarts实例
 * 组件卸载时销毁echarts实例
 * @param config 配置项
 * @param config.locale locale语言环境
 * @param config.initialOption 初始化配置项（一般指静态echarts配置项，不依赖异步获取的数据的配置项）
 * @param config.setOptionConfigs setOption方法的第二个参数，具体定义参见echarts官网
 */
export default function useChart(config: {
  locale?: 'en-US' | 'zh-CN' | string;
  initialOption?: IEChartOption;
  setOptionConfigs?: ISetOptionConfigs;
}) {
  const { locale, initialOption, setOptionConfigs } = config || {};

  /**
   * chart的dom
   */
  const chartNode = useRef<HTMLDivElement | null>(null);

  /**
   * chart实例
   */
  const chartInstance = useRef<EChartsType | null>(null);

  /**
   * chart语言环境（echarts内部语言环境）
   */
  const chartLocale = useMemo<IEChartLocale>(() => {
    if (!locale) return DEFAULT_ECHARTS_LOCALE;
    return ECHARTS_LOCALE_MAP[locale] || DEFAULT_ECHARTS_LOCALE;
  }, [locale]);

  /**
   * chart配置
   */
  const [option, setOption] = useState<IEChartOption>(() => cloneDeep(initialOption) || {});

  /**
   * 组件加载时监听window的resize事件，处理echarts的resize功能
   * 组件卸载时移除window的resize事件监听器
   */
  useChartResize(chartInstance);

  /**
   * echarts配置项变化时,更新echarts图表
   */
  useChartRefreshWhenOptionChanged(chartInstance, option, setOptionConfigs);

  /**
   * 获取chart实例
   */
  const getChartInstance = () => {
    return chartInstance.current ?? null;
  };

  /**
   * 初始化chart实例
   */
  useEffect(() => {
    if (!chartNode.current) return;
    if (chartInstance.current) return;

    // @ts-ignore
    chartInstance.current = echarts.init(chartNode.current, undefined, {
      ...ECHARTS_INIT_CONFIGS,
      locale: chartLocale,
    });

    /**
     * 组件卸载时，销毁echarts实例
     */
    return () => {
      if (!!chartInstance.current && !chartInstance.current.isDisposed()) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, [chartLocale]);

  return {
    chartNode,
    chartInstance,
    option,
    setOption,
    getChartInstance,
  };
}
